// tslint:disable:no-commented-code
// tslint:disable:only-arrow-functions

import {
  IDynamicBindableValue,
  IDynamicBindableValueCombination,
  isIDynamicBindableValue,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { State as AccountsState } from '@epicuro-next/state/accounts-state';

export interface PanelTrackingIdConstants {
  page: string;
  location: string;
  type: string;
  provider: string;
  cssVariant: string;
}

export interface PanelTrackingIdVariables {
  brandId: string;
  brandOrganisationId: string;
  accountId: string;
}

export function getPanelTrackingIdAsString(
  constants: PanelTrackingIdConstants,
  variables: PanelTrackingIdVariables,
): string {
  const c = constants;
  const v = variables;
  const panelTrackingId = `${c.page}${c.location}${c.type}${c.provider}${v.brandOrganisationId}${v.accountId}${v.brandId}${c.cssVariant}`;
  return panelTrackingId.toLowerCase();
}

export function getPanelTrackingIdProperty(constants: IDynamicBindableValue): {
  panelTrackingId: IDynamicBindableValueCombination;
};
export function getPanelTrackingIdProperty(
  constants: PanelTrackingIdConstants,
): {
  panelTrackingId: IDynamicBindableValueCombination;
  panelTrackingIdConstants: PanelTrackingIdConstants;
};
export function getPanelTrackingIdProperty(
  constants: PanelTrackingIdConstants | IDynamicBindableValue,
):
  | {
      panelTrackingId: IDynamicBindableValueCombination;
      panelTrackingIdConstants: PanelTrackingIdConstants;
    }
  | {
      panelTrackingId: IDynamicBindableValueCombination;
    } {
  const panelTrackingIdConstantsProvidedAsBindableValue =
    isIDynamicBindableValue(constants);

  const values: IDynamicBindableValue[] = [];

  const panelTrackingIdConstantsProvider: IDynamicBindableValue =
    panelTrackingIdConstantsProvidedAsBindableValue
      ? constants
      : {
          provider: 'self',
          property: 'panelTrackingIdConstants',
        };

  values.push(
    ...[
      panelTrackingIdConstantsProvider,
      {
        provider: 'GlobalStoreProviderService',
        property: {
          once: true,
          paths: 'accounts.activeAccount',
        },
        transform: function (
          activeAccount: AccountsState['activeAccount'],
        ): PanelTrackingIdVariables {
          if (!activeAccount) {
            throw new Error(
              'Active account is not defined hence cannot define panel style.',
            );
          }

          const brandOrgs = activeAccount.brandOrganisations;
          if (!brandOrgs || brandOrgs.length === 0) {
            throw new Error(
              'Active account does not seem to have any brand organisations. Hence panel style cannot be defined.',
            );
          }

          const brandOrg = brandOrgs[0];
          if (!brandOrg) {
            throw new Error(
              'Brand organisation entry is not defined. Hence panel style cannot be defined.',
            );
          }

          const returnValue = {
            accountId: activeAccount.externalId,
            brandOrganisationId: brandOrg.brandOrganisationId,
            brandId: brandOrg.brandId,
          };

          return returnValue;
        }.toString(),
      },
    ],
  );

  const bindableValue: IDynamicBindableValueCombination = {
    values,
    transform: function ([panelTrackingIdConstants, panelTrackingIdVariables]: [
      PanelTrackingIdConstants,
      PanelTrackingIdVariables,
      string,
    ]): string {
      return window.neoclesDynamicFramework.recommendationsPanels.methods.getPanelTrackingId(
        panelTrackingIdConstants,
        panelTrackingIdVariables,
      );
    }.toString(),
  };

  if (!panelTrackingIdConstantsProvidedAsBindableValue) {
    return {
      panelTrackingIdConstants: constants,
      panelTrackingId: bindableValue,
    };
  } else {
    return {
      panelTrackingId: bindableValue,
    };
  }
}

export const panelTrackingIdPropertyName: keyof ReturnType<
  typeof getPanelTrackingIdProperty
> = 'panelTrackingId';
